import React from "react";
import styled from "styled-components";
import { useParams, useHistory } from "react-router-dom";
import routes from "routes";

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 24px 32px;
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const BreadcrumbBar = styled.div`
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;

  &:hover {
    background: #ffffff;
    color: #0f172a;
  }
`;

const HeaderCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 32px;
  margin-bottom: 24px;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px;
  margin-bottom: 28px;

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const ProviderInfo = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex: 1;
`;

const Avatar = styled.div`
  width: 72px;
  height: 72px;
  background: #0ea5e9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 28px;
  flex-shrink: 0;
`;

const ProviderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const ProviderName = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const ProviderMeta = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;

  strong {
    color: #475569;
    font-weight: 600;
  }
`;

const Divider = styled.span`
  color: #cbd5e1;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  background: ${(p) => (p.active ? "#dcfce7" : "#fee2e2")};
  color: ${(p) => (p.active ? "#166534" : "#991b1b")};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 968px) {
    width: 100%;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  outline: none;

  ${(p) =>
    p.variant === "primary"
      ? `
    background: #0ea5e9;
    color: #ffffff;
    &:hover {
      background: #0284c7;
    }
  `
      : `
    background: #f8fafc;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    &:hover {
      background: #ffffff;
      border-color: #cbd5e1;
    }
  `}

  &:focus {
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 968px) {
    flex: 1;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #0f172a;
  font-weight: 500;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1200px) {
    order: -1;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const CardCount = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const CardBody = styled.div`
  padding: 24px;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const ServiceCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ServiceName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const ServiceStatus = styled.span`
  padding: 4px 10px;
  background: ${(p) => (p.active ? "#dcfce7" : "#fee2e2")};
  color: ${(p) => (p.active ? "#166534" : "#991b1b")};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ServiceDetail = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  gap: 12px;
`;

const DetailLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #0f172a;
  font-weight: 600;
  text-align: right;
`;

const StatsCard = styled(Card)``;
const StatsList = styled.div`
  display: flex;
  flex-direction: column;
`;
const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;

  &:first-child { padding-top: 0; }
  &:last-child { border-bottom: none; padding-bottom: 0; }
`;
const StatLabel = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;
const StatValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const ViewServiceProvider = () => {
  const { id } = useParams();
  const history = useHistory();

  const provider = {
    id,
    name: id === "1" ? "Alpha Diagnostics" : id === "2" ? "MediLab Services" : "RoadRescue Ltd",
    email: id === "1" ? "support@alpha.com" : id === "2" ? "hello@medilab.com" : "support@roadrescue.co.ke",
    phone: id === "3" ? "+254 700 123 456" : "+254 711 111 111",
    status: id === "2" ? "Inactive" : "Active",
    since: "March 12, 2024"
  };

  const services = (
    id === "3"
      ? [
          { id: "srv-1", name: "Towing", status: "Active", coverage: "Nationwide", sla: "45 mins avg", availability: "24/7" },
          { id: "srv-2", name: "Roadside Assistance", status: "Active", coverage: "Urban Centers", sla: "30 mins avg", availability: "24/7" },
        ]
      : [
          { id: "srv-1", name: "Diagnostics", status: "Active", coverage: "Nairobi & Kiambu", sla: "24 hrs", availability: "Mon-Fri" },
          { id: "srv-2", name: "Laboratory Tests", status: id === "2" ? "Inactive" : "Active", coverage: "Countrywide Partners", sla: "48 hrs", availability: "Mon-Sat" },
        ]
  );

  const getInitials = (name) => (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Page>
      <Container>
        <BreadcrumbBar>
          <BackButton onClick={() => history.push(routes.serviceProviders)}>
            ← Back to Providers
          </BackButton>
        </BreadcrumbBar>

        <HeaderCard>
          <HeaderTop>
            <ProviderInfo>
              <Avatar>{getInitials(provider.name)}</Avatar>
              <ProviderDetails>
                <ProviderName>{provider.name}</ProviderName>
                <ProviderMeta>
                  <MetaItem>
                    <strong>Provider ID:</strong> {provider.id}
                  </MetaItem>
                  <Divider>•</Divider>
                  <MetaItem>
                    <strong>Status:</strong> <StatusBadge active={provider.status === "Active"}>{provider.status}</StatusBadge>
                  </MetaItem>
                </ProviderMeta>
              </ProviderDetails>
            </ProviderInfo>

            <QuickActions>
              <Button variant="primary">Edit Provider</Button>
              <Button>Disable</Button>
            </QuickActions>
          </HeaderTop>

          <ContactInfo>
            <InfoItem>
              <InfoLabel>Email Address</InfoLabel>
              <InfoValue>{provider.email}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Phone Number</InfoLabel>
              <InfoValue>{provider.phone}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Onboarded</InfoLabel>
              <InfoValue>{provider.since}</InfoValue>
            </InfoItem>
          </ContactInfo>
        </HeaderCard>

        <ContentGrid>
          <MainColumn>
            <Card>
              <CardHeader>
                <CardTitle>Services Offered</CardTitle>
                <CardCount>{services.length} Active</CardCount>
              </CardHeader>
              <CardBody>
                <ServicesGrid>
                  {services.map((srv) => (
                    <ServiceCard key={srv.id}>
                      <ServiceHeader>
                        <ServiceName>{srv.name}</ServiceName>
                        <ServiceStatus active={srv.status === "Active"}>{srv.status}</ServiceStatus>
                      </ServiceHeader>
                      <ServiceDetail>
                        <DetailLabel>Coverage</DetailLabel>
                        <DetailValue>{srv.coverage}</DetailValue>
                      </ServiceDetail>
                      <ServiceDetail>
                        <DetailLabel>SLA</DetailLabel>
                        <DetailValue>{srv.sla}</DetailValue>
                      </ServiceDetail>
                      <ServiceDetail>
                        <DetailLabel>Availability</DetailLabel>
                        <DetailValue>{srv.availability}</DetailValue>
                      </ServiceDetail>
                    </ServiceCard>
                  ))}
                </ServicesGrid>
              </CardBody>
            </Card>
          </MainColumn>

          <SideColumn>
            <StatsCard>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
              </CardHeader>
              <CardBody>
                <StatsList>
                  <StatItem>
                    <StatLabel>Total Services</StatLabel>
                    <StatValue>{services.length}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Active Services</StatLabel>
                    <StatValue>{services.filter((s) => s.status === "Active").length}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Avg. SLA</StatLabel>
                    <StatValue>{services.some((s) => s.sla.includes("min")) ? "~40 mins" : "~36 hrs"}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Coverage Breadth</StatLabel>
                    <StatValue>{services.map(s => s.coverage).includes("Nationwide") ? "Nationwide" : "Regional"}</StatValue>
                  </StatItem>
                </StatsList>
              </CardBody>
            </StatsCard>
          </SideColumn>
        </ContentGrid>
      </Container>
    </Page>
  );
};

export default ViewServiceProvider;

